import {
  AGG_INFIX,
  AGG_WORDS,
  ANTONYMS,
  BILLABLE_WORDS,
  BILLING_WORDS,
  BUCKETS,
  CHANGED_WORDS,
  CHANGES_OF,
  COMPANY_WORDS,
  COMPARATOR_FIELDS,
  COMPARATORS,
  COUNT_WORDS,
  CREATED_WORDS,
  CREATION_FIELDS,
  DAY_WORDS,
  DIM_OF_FIELD,
  DURATION_HINTS,
  FIELD_NAMES,
  fixedLocale,
  FRESH_WORDS,
  INTENT_WORDS,
  GROUP_MARKERS,
  HOW_MUCH,
  LAST_WORDS,
  LIST_WORDS,
  MONTH_WORDS,
  MONTHS,
  NEGATION,
  NEXT_WORDS,
  RANGE_JOIN,
  RANGE_OPEN,
  REFINES,
  SET_NAMES,
  STOP,
  TIME_FIELD_HINTS,
  TIME_PHRASES,
  TOP_WORDS,
  UNITS,
  UNNAMED_SETS,
  WEEK_WORDS,
} from './lexicon'
import { distance, fold, stem, stemPhrase, tokenize, typoBudget, type Token } from './text'
import type { Bucket, CatalogField, CatalogSet, Definition, Filter, Locale, Measure, Period, Restrict, ValueIndex, ValueItem, View, Words } from './types'

/**
 * BARA "CE VREI SA VEZI?" - FRAZA -> DEFINITIE, FARA NICIUN MODEL.
 *
 * Parserul e determinist: aceeasi fraza, acelasi catalog si aceleasi valori
 * dau mereu aceeasi definitie. Tiparele:
 *
 *   CE      setul ("tichete", "calculatoare") + filtre ("deschise",
 *           "prioritate Mare", "cu Windows 7", "doar Bucuresti", "fara Cluj")
 *   CAT     "cate", "media timpului de rezolvare", "% SLA", "top 10"
 *   CAND    "azi", "luna trecuta", "ultimele 30 de zile", "Q3", "in martie"
 *   PE CE   "pe sediu", "pe luna", "by technician"
 *
 * Cand nu intelege, NU GHICESTE: un cuvant necunoscut intoarce `unknown` cu
 * variante apropiate, iar o valoare care e si sediu si grup intoarce `ask`, cu
 * cate o definitie gata facuta pentru fiecare varianta. Tinta corpusului de
 * probe (`parser.test.ts`) e zero fraze ghicite gresit.
 *
 * Serverul nu se bizuie pe parser: definitia trece prin compilator, care
 * refuza orice cheie necunoscuta si pune singur perimetrul.
 */

export interface ParseContext {
  catalog: CatalogSet[]
  values: ValueIndex
  now: Date
  /** Limba ecranului: variantele propuse pentru un cuvant necunoscut vin numai din ea. */
  locale?: Locale
}

export interface UnknownWord {
  word: string
  start: number
  end: number
  suggestions: string[]
  /** De ce nu se poate: cuvantul e inteles, dar setul n-are ce-i trebuie ("nou" fara data de adaugare). */
  reason?: Words
}

export interface AskOption {
  label: Words
  definition: Definition
}

export interface Ask {
  text: Words
  options: AskOption[]
}

export interface ParseResult {
  status: 'ok' | 'ask' | 'unknown' | 'empty'
  definition?: Definition
  question?: Ask
  unknown: UnknownWord[]
  corrections: { from: string; to: string }[]
}

/* ========================================================================= */

interface Entry {
  stems: string[]
  /** Cum se scrie, pentru corecturi si sugestii. */
  surface: string
  /** Toate felurile in care e scris in vocabular ("inchis", "inchise"), pentru greselile de tastare. */
  forms: string[]
  kind: 'dataset' | 'field' | 'fieldagg' | 'bool' | 'antonym'
  field?: string
  agg?: Measure['agg']
  value?: 0 | 1
}

interface ValueRef {
  field: string
  stems: string[]
  words: string[]
  item: ValueItem
}

interface Pending {
  value: { field: string; ids: number[]; text: string; negate: boolean }[]
  ambiguity?: { start: number; end: number; text: string; choices: { field: string; ids: number[] }[]; negate: boolean }
}

interface Attempt {
  set: CatalogSet
  definition: Definition
  unknown: number[]
  ask: Ask | null
  consumed: number
  corrections: { from: string; to: string }[]
  /** Cuvintele de timp ramase fara inteles fiindca setul n-are camp de timp. */
  timeless: number[]
  /** Explicatiile cuvintelor intelese, dar fara loc in setul asta. */
  reasons: Map<number, Words>
}

const DEFAULT_TOP = 10

/** O conditie numerica citita din fraza: "peste 8 ore" -> gt, 28800 secunde. */
interface Comparison {
  op: 'gt' | 'gte' | 'lt' | 'lte'
  value: number
  unit: 'duration' | 'bytes' | 'percent' | null
  /** Campul spus de comparatia insasi ("older than" -> vechimea). */
  field?: string
}

/** Parserul, cu cache pe catalog: lexiconul unui set se face o singura data. */
export class PhraseParser {
  private readonly entries = new Map<string, Entry[]>()

  private readonly valueRefs = new Map<string, ValueRef[]>()

  private readonly ctx: ParseContext

  /** Cuvintele frazei curente care arata a nume (litera mare, cifra, punct): vezi `nameLike`. */
  private names = new Set<Token>()

  constructor(ctx: ParseContext) {
    this.ctx = ctx
    for (const set of ctx.catalog) {
      this.entries.set(set.key, buildEntries(set))
      this.valueRefs.set(set.key, buildValues(set, ctx.values[set.key] ?? {}))
    }
  }

  parse(text: string): ParseResult {
    const tokens = tokenize(text)
    this.names = nameLike(text, tokens)
    if (tokens.filter((t) => !STOP.has(t.stem)).length === 0) {
      return { status: 'empty', unknown: [], corrections: [] }
    }

    let named = this.namedSets(tokens)
    /*
     * "ce s-a schimbat in cmdb", "what changed in cmdb": setul numit are un set
     * de schimbari, si fraza intreaba de schimbari - se citeste acolo, cu
     * numele setului de baza inteles ca nume. "schimbari" nu mai numeste
     * atunci schimbarile ITIL: cuvantul a fost luat de intrebare.
     */
    const aliases = new Map<string, CatalogSet>()
    const changedAt = new Set<number>()
    tokens.forEach((_, i) => {
      const n = longestSet(tokens, i, CHANGED_WORDS)
      for (let k = 0; k < n; k++) changedAt.add(i + k)
    })
    if (changedAt.size > 0) {
      const rest = this.namedSets(tokens, changedAt)
      for (const base of rest) {
        const target = this.ctx.catalog.find((s) => s.key === CHANGES_OF[base.key])
        if (!target || named.includes(target)) continue
        named = [...rest.filter((s) => s !== base), target]
        aliases.set(target.key, base)
        break
      }
    }
    const unnamed = this.ctx.catalog.filter((s) => UNNAMED_SETS.has(s.key))
    let attempts = (named.length > 0 ? named : unnamed).map((set) => this.attempt(set, tokens, aliases.get(set.key)))
    let good = attempts.filter((a) => a.unknown.length === 0)
    /*
     * "calculatoarele cu Adobe Reader": calculatoarele nu au aplicatii, dar
     * aplicatiile au calculatoare. Cand niciun set numit nu intelege tot, se
     * incearca toate - si se primeste numai un raspuns unic.
     */
    const timeOnly = attempts.some((a) => a.unknown.length > 0 && a.unknown.every((k) => a.timeless.includes(k)))
    if (good.length === 0 && named.length > 0 && !timeOnly) {
      const rest = unnamed.filter((s) => !named.includes(s)).map((set) => this.attempt(set, tokens))
      const restGood = rest.filter((a) => a.unknown.length === 0 && !a.ask)
      if (restGood.length === 1) {
        good = restGood
        attempts = [...attempts, ...rest]
      }
    }
    /* Fara set numit, o incercare care mai are si ea o intrebare nu e un raspuns. */
    if (named.length === 0) good = good.filter((a) => !a.ask)

    /*
     * "biled last month", "facturte": niciun set numit si niciun raspuns - un
     * nume de set scris gresit, daca e unul singur aproape, se incearca.
     */
    if (good.length === 0 && named.length === 0) {
      const typoSets = this.typoSets(tokens).filter((s) => !UNNAMED_SETS.has(s.key))
      if (typoSets.length === 1) {
        const a = this.attempt(typoSets[0]!, tokens)
        attempts = [a, ...attempts]
        if (a.unknown.length === 0 && !a.ask) good = [a]
      }
    }

    /*
     * "top 10 echipamente dupa cost": "cost" numeste costul serviciilor, dar
     * "echipamente dupa cost" numeste mai precis costul pe echipament - cand
     * amandoua inteleg tot, ramane setul numit de expresia cea mai lunga.
     */
    if (good.length > 1) {
      const strength = (set: CatalogSet) => this.nameLength(tokens, set)
      const best = Math.max(...good.map((a) => strength(a.set)))
      if (best >= 2) good = good.filter((a) => strength(a.set) === best)
    }

    /*
     * SLA rafineaza tichetele: cand amandoua inteleg fraza, ramane SLA daca a
     * fost pomenit, si tichetele daca nu.
     */
    if (good.length > 1) {
      good = good.filter((a) => {
        const refinedBy = good.find((b) => REFINES[b.set.key] === a.set.key)
        if (refinedBy && named.includes(refinedBy.set)) return false
        const base = REFINES[a.set.key]
        return !(base && !named.includes(a.set) && good.some((b) => b.set.key === base))
      })
    }
    if (good.length > 1) {
      const best = Math.max(...good.map((a) => a.consumed))
      good = good.filter((a) => a.consumed === best)
    }

    if (good.length === 1) {
      const chosen = good[0]!
      if (chosen.ask) {
        return { status: 'ask', question: chosen.ask, unknown: [], corrections: chosen.corrections }
      }
      return { status: 'ok', definition: chosen.definition, unknown: [], corrections: chosen.corrections }
    }

    if (good.length > 1) {
      return {
        status: 'ask',
        question: {
          text: { ro: 'Despre ce date e vorba?', en: 'Which data do you mean?' },
          options: good.map((a) => ({ label: a.set.label, definition: a.definition })),
        },
        unknown: [],
        corrections: [],
      }
    }

    /* Nimeni nu intelege tot: se arata cuvintele ramase, din incercarea cea mai apropiata. */
    const closest = attempts.reduce((a, b) => (b.unknown.length < a.unknown.length ? b : a), attempts[0]!)
    const unknownIdx = closest ? closest.unknown : tokens.map((_, i) => i)
    return {
      status: 'unknown',
      unknown: unknownIdx.map((i) => {
        const t = tokens[i]!
        const reason = closest?.reasons.get(i)
        return { word: text.slice(t.start, t.end), start: t.start, end: t.end, suggestions: reason ? [] : this.suggest(t.word, closest?.set), ...(reason ? { reason } : {}) }
      }),
      corrections: [],
    }
  }

  /** Cheile seturilor numite in fraza - pentru a le aduce valorile reale. */
  namedKeys(text: string): string[] {
    return this.namedSets(tokenize(text)).map((s) => s.key)
  }

  /** Cel mai lung nume al setului gasit in fraza, in cuvinte. */
  private nameLength(tokens: Token[], set: CatalogSet): number {
    let best = 0
    for (const e of this.entries.get(set.key) ?? []) {
      if (e.kind !== 'dataset' || e.stems.length <= best) continue
      if (tokens.some((_, i) => matchAt(tokens, i, e.stems))) best = e.stems.length
    }
    return best
  }

  /** Seturile al caror nume (un cuvant) e la o greseala de tastare de un cuvant din fraza. */
  private typoSets(tokens: Token[]): CatalogSet[] {
    const out: CatalogSet[] = []
    for (const set of this.ctx.catalog) {
      const names = (this.entries.get(set.key) ?? []).filter((e) => e.kind === 'dataset' && e.stems.length === 1)
      const hit = tokens.some((t) => {
        if (STOP.has(t.stem)) return false
        const budget = Math.min(typoBudget(t.word), t.stem.length >= 8 ? 2 : 1)
        if (budget === 0) return false
        return names.some((e) => distance(t.stem, e.stems[0]!, budget) <= budget || e.forms.some((f) => !f.includes(' ') && distance(t.word, f, budget) <= budget))
      })
      if (hit) out.push(set)
    }
    return out
  }

  /** Seturile numite in fraza prin numele sau sinonimele lor; `skip` = locurile care nu numesc nimic. */
  private namedSets(tokens: Token[], skip?: Set<number>): CatalogSet[] {
    const out: CatalogSet[] = []
    const free = (i: number, n: number) => !skip || !Array.from({ length: n }, (_, k) => i + k).some((k) => skip.has(k))
    for (const set of this.ctx.catalog) {
      const names = (this.entries.get(set.key) ?? []).filter((e) => e.kind === 'dataset')
      const hit = tokens.some((_, i) => names.some((e) => matchAt(tokens, i, e.stems) && free(i, e.stems.length)))
      if (hit) out.push(set)
    }
    return out
  }

  /**
   * Variante pentru un cuvant necunoscut: cuvinte din vocabular, apropiate ca
   * scriere - numai din limba ecranului (si valorile reale, care n-au limba).
   * In engleza nu se propune "fel", in romana nu "next month".
   */
  suggest(word: string, set?: CatalogSet): string[] {
    const pool: string[] = []
    const sets = set ? [set] : this.ctx.catalog
    const langs: Locale[] = this.ctx.locale ? [this.ctx.locale] : ['ro', 'en']
    for (const s of sets) {
      for (const l of langs) pool.push(...s.syn[l], s.label[l])
      for (const f of s.fields) {
        for (const l of langs) pool.push(...f.syn[l])
        for (const item of this.ctx.values[s.key]?.[f.key] ?? []) pool.push(item.label)
        for (const o of f.options ?? []) for (const l of langs) pool.push(o.label[l])
      }
    }
    for (const [w] of TIME_PHRASES) {
      const l = fixedLocale(w)
      if (l === 'both' || langs.includes(l)) pool.push(w)
    }
    for (const l of langs) pool.push(...INTENT_WORDS[l])
    const seen = new Set<string>()
    const scored: [number, string][] = []
    for (const candidate of pool) {
      const folded = candidate.toLowerCase()
      if (seen.has(folded)) continue
      seen.add(folded)
      const d = distance(word, folded.split(' ')[0] ?? folded, 2)
      if (d <= 2) scored.push([d, candidate])
    }
    scored.sort((a, b) => a[0] - b[0] || a[1].localeCompare(b[1]))
    return scored.slice(0, 3).map(([, s]) => s)
  }

  /* ======================================================================= */

  private attempt(set: CatalogSet, tokens: Token[], alias?: CatalogSet): Attempt {
    /* Setul de schimbari citit in locul setului de baza: numele bazei raman nume ("cmdb", "ci"). */
    const own = this.entries.get(set.key) ?? []
    const entries = alias ? [...own, ...(this.entries.get(alias.key) ?? []).filter((e) => e.kind === 'dataset')] : own
    const changesSet = Object.values(CHANGES_OF).includes(set.key)
    const reasons = new Map<number, Words>()
    /* "nou", "new", "adaugate": randurile create in perioada. */
    const freshTokens: number[] = []
    let freshByField = false
    const values = this.valueRefs.get(set.key) ?? []
    const fields = new Map(set.fields.map((f) => [f.key, f]))
    const unknown: number[] = []
    const corrections: { from: string; to: string }[] = []

    const filters: Filter[] = []
    const restrict: Restrict = {}
    let period: Period | undefined
    const groups: { field: string; bucket?: Bucket }[] = []
    const measures: Measure[] = []
    const mentioned: string[] = []
    let pendingAgg: Measure['agg'] | null = null
    let counting = false
    /* "cat am facturat", "how much": suma setului, nu numarul randurilor. */
    let howMuch = false
    let listing = false
    let top: number | null = null
    let negate = false
    let pendingField: string | null = null
    /*
     * "imprimanta Xerox", "utilizator john.doe": dupa numele unui camp de text,
     * cuvintele pe care nu le stie nimeni sunt chiar valoarea cautata - un nume
     * de imprimanta sau de cont care nu e (inca) printre valorile aduse de
     * server. Numai un cuvant care arata a nume (`nameLike`: "Xerox",
     * "john.doe", "PC-07") - "calculatoare lipseste", "profile distincte" raman
     * cuvinte de inteles, nu valori. `freeAt` e pozitia de unde poate incepe textul liber; `freeFilter`
     * e filtrul in care se aduna cuvintele la rand.
     */
    let freeAt = -1
    let freeFilter: Filter | null = null
    let lastGroup = false
    let countEnd = -1
    /* "top 5 probleme DUPA incidente legate": campul numeric dupa care se ordoneaza. */
    let sortField: string | null = null
    /* Campul care poarta numele setului ("aplicatii folosite" -> executabilul). */
    let datasetField: string | null = null
    /* Ultimul filtru da/nu pus dintr-un nume de camp: "change SUCCESS rate" il face procent. */
    let lastBool: number | null = null
    /* Conditiile numerice care n-au gasit singure campul: se intreaba la sfarsit. */
    const openComparisons: { cmp: Comparison; candidates: string[]; start: number; end: number }[] = []
    const periodTokens: number[] = []
    let consumed = 0
    let ambiguity: Pending['ambiguity']

    const textValues = new Map<string, { labels: string[]; neg: boolean }>()
    const addValue = (field: CatalogField, ids: number[], labels: string[], text: string, neg: boolean) => {
      const dim = DIM_OF_FIELD[field.key]
      if (field.type === 'enum' && ids.length > 0) {
        if (!neg && dim && set.dims.includes(dim)) {
          const list = (restrict[dim] ??= [])
          for (const id of ids) if (!list.includes(id)) list.push(id)
          return
        }
        /* "High sau Urgenta": aceeasi lista, nu doua filtre care s-ar exclude. */
        const same = filters.find((f) => f.field === field.key && f.op === (neg ? 'nin' : 'in'))
        if (same && Array.isArray(same.value)) {
          for (const id of ids) if (!same.value.includes(id)) same.value.push(id)
          return
        }
        filters.push({ field: field.key, op: neg ? 'nin' : 'in', value: [...ids] })
        return
      }
      /*
       * Text: o valoare = "contine" cuvintele omului. A doua valoare pe acelasi
       * camp ("Chrome sau Firefox") face din amandoua o lista de etichete
       * intregi, ca sa nu se ceara doua lucruri deodata.
       */
      const prev = textValues.get(field.key)
      if (prev && prev.neg === neg) {
        const idx = filters.findIndex((f) => f.field === field.key && (f.op === 'contains' || f.op === 'ncontains' || f.op === 'in' || f.op === 'nin'))
        for (const l of labels) if (!prev.labels.includes(l)) prev.labels.push(l)
        const merged: Filter = { field: field.key, op: neg ? 'nin' : 'in', value: [...prev.labels] }
        if (idx >= 0) filters[idx] = merged
        return
      }
      textValues.set(field.key, { labels: [...labels], neg })
      filters.push({ field: field.key, op: neg ? 'ncontains' : 'contains', value: text })
    }

    let i = 0
    while (i < tokens.length) {
      const t = tokens[i]!

      /* ---- tipare cu numere: ultimele N zile, top N, Q3, luna, interval de date ---- */
      const pattern = this.pattern(tokens, i)
      if (pattern) {
        if (pattern.compare) {
          const cmp = pattern.compare
          /* Campul: cel abia pomenit, cel care urmeaza, cel sugerat de filtre, sau singurul de felul lui. */
          let field: string | null = pendingField !== null && fitsComparison(fields.get(pendingField), cmp) ? pendingField : null
          /* "mai vechi de 7 zile": vechimea, cand setul o are. */
          if (field === null && cmp.field && fitsComparison(fields.get(cmp.field), cmp)) field = cmp.field
          let extra = 0
          if (field === null) {
            let j = i + pattern.length
            if (tokens[j] && STOP.has(tokens[j]!.stem)) j++
            const next = this.entryAt(entries, tokens, j, ['field'], false)
            if (next && next.entry.field && fitsComparison(fields.get(next.entry.field), cmp)) {
              field = next.entry.field
              extra = j - (i + pattern.length) + next.length
              mentioned.push(field)
            }
          }
          const candidates = set.fields.filter((f) => fitsComparison(f, cmp)).map((f) => f.key)
          if (field === null && cmp.unit === 'duration') {
            const hint = DURATION_HINTS.find((h) => filters.some((f) => f.field === h.field && f.op === 'eq' && f.value === h.value) && candidates.includes(h.duration))
            if (hint) field = hint.duration
          }
          if (field === null && candidates.length === 1) field = candidates[0]!
          if (field !== null) {
            filters.push({ field, op: cmp.op, value: cmp.value })
            pendingField = null
          } else if (candidates.length > 0) {
            openComparisons.push({ cmp, candidates, start: i, end: i + pattern.length })
          } else {
            for (let k = 0; k < pattern.length; k++) unknown.push(i + k)
          }
          i += pattern.length + extra
          consumed += pattern.length + extra
          continue
        }
        if (pattern.period) {
          period = pattern.period
          for (let k = 0; k < pattern.length; k++) periodTokens.push(i + k)
        }
        if (pattern.top !== undefined) top = pattern.top
        i += pattern.length
        consumed += pattern.length
        continue
      }

      /* ---- "pe sediu", "pe luna": gruparea ---- */
      const marker = longestSet(tokens, i, GROUP_MARKERS)
      if (marker > 0) {
        const after = i + marker
        const bucket = bucketAt(tokens, after)
        if (bucket && set.time) {
          groups.push({ field: set.time, bucket: bucket.bucket })
          i = after + bucket.length
          consumed += marker + bucket.length
          continue
        }
        const fieldHit = this.entryAt(entries, tokens, after, ['field'])
        /* "top 10 licente DUPA deficit": un camp numeric dupa "dupa"/"by" e ordinea, nu gruparea. */
        const numericHit = fieldHit && fieldHit.entry.field ? fields.get(fieldHit.entry.field) : undefined
        if (numericHit && isNumeric(numericHit) && !numericHit.group && (top !== null || longestSet(tokens, i, SORT_MARKERS) > 0)) {
          sortField = numericHit.key
          i = after + fieldHit!.length
          consumed += marker + fieldHit!.length
          continue
        }
        /* "top 10 calculatoare DUPA alarme": numele setului dupa "dupa" = numarul de randuri. */
        if (top !== null && longestSet(tokens, i, SORT_MARKERS) > 0) {
          const byCount = this.entryAt(entries, tokens, after, ['dataset'], false)
          if (byCount) {
            counting = true
            i = after + byCount.length
            consumed += marker + byCount.length
            continue
          }
        }
        /* "pe calculatoare DIN CLUJ" e un loc, nu o grupare: dupa camp urmeaza o valoare. */
        const located = fieldHit !== null && valueFollows(values, tokens, after + fieldHit.length)
        if (fieldHit && fieldHit.entry.field && fields.get(fieldHit.entry.field)?.group && !located) {
          groups.push({ field: fieldHit.entry.field })
          if (fieldHit.corrected) corrections.push({ from: tokens[after]!.word, to: fieldHit.corrected })
          i = after + fieldHit.length
          consumed += marker + fieldHit.length
          pendingField = null
          lastGroup = true
          continue
        }
      }

      /* "pe categorie SI prioritate": a doua grupare. */
      if (lastGroup && groups.length === 1 && AND_WORDS.has(t.stem)) {
        const second = this.entryAt(entries, tokens, i + 1, ['field'], false)
        if (second && second.entry.field && fields.get(second.entry.field)?.group && second.entry.field !== groups[0]!.field) {
          groups.push({ field: second.entry.field })
          i += 1 + second.length
          consumed += 1 + second.length
          lastGroup = false
          continue
        }
      }
      lastGroup = false

      /* ---- "lunar", "zilnic", "monthly": felia de timp fara "pe" ---- */
      const adverb = ADVERB_BUCKETS.get(t.stem)
      if (adverb && set.time && groups.length === 0 && this.entryAt(entries, tokens, i, undefined, false) === null) {
        groups.push({ field: set.time, bucket: adverb })
        i += 1
        consumed += 1
        continue
      }

      /* ---- timpul, ca expresie ---- */
      const time = longestPhrase(tokens, i, TIME_STEMS)
      if (time) {
        period = { key: time.value }
        for (let k = 0; k < time.length; k++) periodTokens.push(i + k)
        i += time.length
        consumed += time.length
        continue
      }

      /* ---- vocabularul setului si valorile reale ---- */
      const hit = this.entryAt(entries, tokens, i, undefined, false)
      const value = matchValues(values, tokens, i)

      /* "new tickets", "calculatoare noi": nu starea "New", ci create de curand - afara de "status new". */
      /* "cate tichete avem noi", "la noi": pronumele, nu "nou". */
      if (t.word === 'noi' && i > 0 && NOI_PRONOUN.has(tokens[i - 1]!.word)) {
        i += 1
        continue
      }
      const fresh = longestSet(tokens, i, FRESH_WORDS)
      if (
        fresh > 0 &&
        (hit === null || hit.length <= fresh) &&
        (value === null || value.length <= fresh) &&
        !(pendingField !== null && value !== null && value.choices.some((c) => c.field === pendingField))
      ) {
        for (let k = 0; k < fresh; k++) freshTokens.push(i + k)
        i += fresh
        consumed += fresh
        continue
      }

      const preferValue =
        value !== null &&
        (hit === null ||
          value.length > hit.length ||
          (pendingField !== null && value.choices.some((c) => c.field === pendingField)) ||
          /* "directii" e numele campului, nu o bucata din "Directia IT": o valoare intreaga, da. */
          (value.length === hit.length && hit.entry.field !== undefined && value.choices.some((c) => c.field === hit.entry.field) && (value.full || hit.entry.kind !== 'field')))

      if (value !== null && preferValue) {
        let choices = value.choices
        if (pendingField !== null && choices.some((c) => c.field === pendingField)) {
          choices = choices.filter((c) => c.field === pendingField)
        }
        const text = tokens
          .slice(i, i + value.length)
          .map((x) => x.word)
          .join(' ')
        if (choices.length === 1) {
          addValue(fields.get(choices[0]!.field)!, choices[0]!.ids, choices[0]!.labels, text, negate)
        } else if (!ambiguity) {
          ambiguity = { start: i, end: i + value.length, text, choices, negate }
        }
        negate = false
        pendingField = null
        i += value.length
        consumed += value.length
        continue
      }

      if (hit) {
        const f = hit.entry.kind === 'field' ? fields.get(hit.entry.field!) : undefined
        const alsoSetName = entries.some((x) => x.kind === 'dataset' && x.stems.join(' ') === hit.entry.stems.join(' '))
        if (countEnd === i && f && !alsoSetName && f.type !== 'bool' && f.type !== 'date' && f.aggs.includes('count_distinct') && measures.length === 0) {
          /* "cate CALCULATOARE au 7-Zip": se numara calculatoarele, nu instalarile. */
          measures.push({ agg: 'count_distinct', field: f.key })
          counting = false
          i += hit.length
          consumed += hit.length
          continue
        }
        /* "ci adaugate", "devices added": campul de data pomenit, si "nou" fara perioada = ultimele 30 de zile. */
        if (f?.type === 'date' && longestSet(tokens, i, CREATED_WORDS) > 0) freshByField = true
        applyHit(hit.entry)
        i += hit.length
        consumed += hit.length
        const named = pendingField !== null ? fields.get(pendingField) : undefined
        if (named && named.type === 'text' && named.filter && named.values) freeAt = i
        continue
      }

      /* Participiile ramase ("discovered", "aparute"), dupa ce vocabularul setului nu le-a luat. */
      const created = longestSet(tokens, i, CREATED_WORDS)
      if (created > 0) {
        for (let k = 0; k < created; k++) freshTokens.push(i + k)
        i += created
        consumed += created
        continue
      }

      /* "ce s-a schimbat": inteles numai pe un set de schimbari. */
      const changed = longestSet(tokens, i, CHANGED_WORDS)
      if (changed > 0 && changesSet) {
        i += changed
        consumed += changed
        continue
      }
      if (changed > 0) {
        for (let k = 0; k < changed; k++) {
          unknown.push(i + k)
          reasons.set(i + k, {
            ro: `Pentru „${set.label.ro}" nu se tine ce s-a schimbat. Schimbarile se urmaresc pentru elementele CMDB: „ce s-a schimbat in cmdb luna trecuta".`,
            en: `"${set.label.en}" does not keep track of what changed. Changes are tracked for CMDB items: "what changed in cmdb last month".`,
          })
        }
        i += changed
        continue
      }

      /* "facturabil", "nefacturat": semnul nu exista nicaieri - se spune. */
      const billable = longestSet(tokens, i, BILLABLE_WORDS)
      if (billable > 0) {
        for (let k = 0; k < billable; k++) {
          unknown.push(i + k)
          reasons.set(i + k, NO_BILLABLE_FLAG)
        }
        i += billable
        continue
      }
      /* "tichete facturate" pe un set fara bani: unde sunt banii. */
      const billing = longestSet(tokens, i, BILLING_WORDS)
      if (billing > 0) {
        for (let k = 0; k < billing; k++) {
          unknown.push(i + k)
          reasons.set(i + k, noBilling(set))
        }
        i += billing
        continue
      }
      const company = longestSet(tokens, i, COMPANY_WORDS)
      if (company > 0) {
        for (let k = 0; k < company; k++) {
          unknown.push(i + k)
          reasons.set(i + k, {
            ro: `Raportul e pe compania aleasa sus, iar „${set.label.ro}" nu au o coloana de firma. Alege alta companie, sau grupeaza pe sediu ori pe grup.`,
            en: `The report covers the company chosen at the top, and "${set.label.en}" has no company column. Pick another company, or group by office or group.`,
          })
        }
        i += company
        continue
      }

      /* ---- cuvintele de legatura ---- */
      const neg = longestSet(tokens, i, NEGATION)
      if (neg > 0) {
        negate = true
        i += neg
        continue
      }
      const much = t.word === 'cat' ? 1 : longestSet(tokens, i, HOW_MUCH) === 2 ? 2 : 0
      if (much > 0) {
        howMuch = true
        countEnd = i + much
        i += much
        consumed += much
        continue
      }
      const count = longestSet(tokens, i, COUNT_WORDS)
      if (count > 0) {
        counting = true
        countEnd = i + count
        i += count
        consumed += count
        continue
      }
      const agg = longestPhrase(tokens, i, AGG_STEMS)
      if (agg) {
        pendingAgg = agg.value
        i += agg.length
        consumed += agg.length
        continue
      }
      const list = longestSet(tokens, i, LIST_WORDS)
      if (list > 0) {
        listing = true
        i += list
        continue
      }
      const topWord = longestSet(tokens, i, TOP_WORDS)
      if (topWord > 0) {
        top = top ?? DEFAULT_TOP
        i += topWord
        consumed += topWord
        continue
      }
      if (STOP.has(t.stem)) {
        i += 1
        continue
      }

      /* ---- textul liber de dupa numele unui camp de text ---- */
      if (i === freeAt && pendingField !== null && !negate && this.names.has(t)) {
        if (freeFilter) {
          freeFilter.value = `${String(freeFilter.value)} ${t.word}`
        } else {
          freeFilter = { field: pendingField, op: 'contains', value: t.word }
          filters.push(freeFilter)
        }
        freeAt = i + 1
        i += 1
        consumed += 1
        continue
      }

      /* ---- o greseala de tastare, numai fata de vocabularul setului ---- */
      const typo = this.entryAt(entries, tokens, i, undefined, true)
      if (typo) {
        corrections.push({ from: t.word, to: typo.corrected ?? t.word })
        applyHit(typo.entry)
        i += 1
        consumed += 1
        continue
      }
      unknown.push(i)
      i += 1
    }

    function applyHit(e: Entry): void {
        if (e.kind === 'dataset') {
          /* Numele setului poate fi si numele unui camp (aplicatia, calculatorul). */
          const asField = entries.find((x) => x.kind === 'field' && x.stems.join(' ') === e.stems.join(' '))
          pendingField = asField?.field ?? null
          if (asField?.field) datasetField = asField.field
        } else if (e.kind === 'field') {
          const f = fields.get(e.field!)!
          if (pendingAgg && isNumeric(f) && f.aggs.includes(pendingAgg)) {
            measures.push({ agg: pendingAgg, field: f.key })
            pendingAgg = null
          } else if (pendingAgg === 'ratio' && f.type === 'bool') {
            measures.push({ agg: 'ratio', field: f.key })
            pendingAgg = null
          } else if (f.type === 'bool') {
            lastBool = negate ? null : filters.length
            filters.push({ field: f.key, op: 'eq', value: negate ? 0 : 1 })
            negate = false
          } else {
            pendingField = f.key
            mentioned.push(f.key)
          }
        } else if (e.kind === 'fieldagg') {
          /* "media orelor facturate": socoteala spusa bate pe cea din nume. */
          const f = fields.get(e.field!)
          const agg = pendingAgg && pendingAgg !== 'ratio' && f?.aggs.includes(pendingAgg) ? pendingAgg : e.agg!
          if (agg === pendingAgg) pendingAgg = null
          measures.push({ agg, field: e.field! })
        } else if (e.kind === 'antonym') {
          const v = negate ? (e.value === 1 ? 0 : 1) : e.value!
          filters.push({ field: e.field!, op: 'eq', value: v })
          negate = false
        }
    }

    /* "Nou" = creat in perioada: pe campul de creare al setului, implicit ultimele 30 de zile. */
    const creation = CREATION_FIELDS.map((k) => set.fields.find((f) => f.key === k && f.type === 'date')).find((f) => f !== undefined)
    let freshField: string | null = null
    if (freshTokens.length > 0 && creation) {
      freshField = creation.key
      period ??= { key: 'last_30_days' }
    } else if (freshTokens.length > 0) {
      for (const k of freshTokens) {
        unknown.push(k)
        reasons.set(k, {
          ro: `„${set.label.ro}" nu au o data a adaugarii, deci nu se poate spune ce e nou.`,
          en: `"${set.label.en}" has no date added, so there is no telling what is new.`,
        })
      }
    } else if (freshByField) {
      period ??= { key: 'last_30_days' }
    }

    /* Un set fara camp de timp nu are perioada: cuvintele de timp raman neintelese. */
    const timeless = period && !set.time && !freshField ? [...periodTokens] : []
    if (timeless.length > 0) {
      unknown.push(...timeless)
      for (const k of timeless) reasons.set(k, { ro: `„${set.label.ro}" nu au o data, deci nici perioada.`, en: `"${set.label.en}" has no date, so no period.` })
      period = undefined
    }

    /* ---- socotelile ramase deschise ---- */
    if (pendingAgg === 'ratio' && lastBool !== null && filters[lastBool]?.op === 'eq') {
      /* "change success rate": campul da/nu a venit inaintea cuvantului "rate". */
      const field = filters[lastBool]!.field
      filters.splice(lastBool, 1)
      measures.push({ agg: 'ratio', field })
      pendingAgg = null
    }
    if (pendingAgg && pendingAgg !== 'ratio' && measures.length === 0) {
      /* "durata medie": socoteala dupa camp. */
      const last = [...mentioned].reverse().map((k) => fields.get(k)).find((f) => f && isNumeric(f) && f.aggs.includes(pendingAgg!))
      if (last) {
        measures.push({ agg: pendingAgg, field: last.key })
        const at = mentioned.lastIndexOf(last.key)
        mentioned.splice(at, 1)
        pendingAgg = null
      }
    }
    if (pendingAgg === 'ratio') {
      const bools = set.fields.filter((f) => f.type === 'bool')
      const field = set.default_measure?.agg === 'ratio' ? set.default_measure.field : bools.length === 1 ? bools[0]!.key : undefined
      if (field) {
        measures.push({ agg: 'ratio', field })
        pendingAgg = null
      }
    }
    if (howMuch && measures.length === 0 && !pendingAgg) {
      if (set.default_measure) measures.push(set.default_measure)
      else counting = true
    }
    if (pendingAgg === 'sum' && measures.length === 0) {
      /* "total" fara camp numeric inseamna numarul de randuri. */
      counting = true
      pendingAgg = null
    }

    let ask: Ask | null = null
    if (pendingAgg) {
      const wanted = pendingAgg
      const numeric = set.fields.filter((f) => (wanted === 'ratio' ? f.type === 'bool' : isNumeric(f)) && f.aggs.includes(wanted))
      if (numeric.length === 1) {
        measures.push({ agg: pendingAgg, field: numeric[0]!.key })
      } else {
        const agg = pendingAgg
        ask = {
          text: { ro: 'Pe ce camp?', en: 'On which field?' },
          options: numeric.map((f) => ({
            label: f.label,
            definition: build({ set, filters, restrict, period, groups, measures: [...measures, { agg, field: f.key }], counting, listing, top, mentioned, sortField, datasetField, freshField }),
          })),
        }
      }
    }

    if (!ask && openComparisons.length > 0) {
      const open = openComparisons[0]!
      if (open.candidates.length <= 6) {
        ask = {
          text: { ro: 'Pe ce camp se aplica conditia?', en: 'Which field does the condition apply to?' },
          options: open.candidates.map((key) => ({
            label: fields.get(key)!.label,
            definition: build({ set, filters: [...filters, { field: key, op: open.cmp.op, value: open.cmp.value }], restrict, period, groups, measures, counting, listing, top, mentioned, sortField, datasetField, freshField }),
          })),
        }
      } else {
        for (let k = open.start; k < open.end; k++) unknown.push(k)
      }
    }

    const definition = build({ set, filters, restrict, period, groups, measures, counting, listing, top, mentioned, sortField, datasetField, freshField })

    if (!ask && ambiguity) {
      const amb = ambiguity
      ask = {
        text: {
          ro: `„${amb.text}" ca ${amb.choices.map((c) => fields.get(c.field)!.label.ro.toLowerCase()).join(' sau ca ')}?`,
          en: `"${amb.text}" as ${amb.choices.map((c) => fields.get(c.field)!.label.en.toLowerCase()).join(' or as ')}?`,
        },
        options: amb.choices.map((c) => {
          const f = fields.get(c.field)!
          const extraFilters = [...filters]
          const extraRestrict: Restrict = JSON.parse(JSON.stringify(restrict)) as Restrict
          const dim = DIM_OF_FIELD[f.key]
          if (f.type === 'enum' && c.ids.length > 0 && !amb.negate && dim && set.dims.includes(dim)) {
            extraRestrict[dim] = [...(extraRestrict[dim] ?? []), ...c.ids]
          } else if (f.type === 'enum' && c.ids.length > 0) {
            extraFilters.push({ field: f.key, op: amb.negate ? 'nin' : 'in', value: c.ids })
          } else {
            extraFilters.push({ field: f.key, op: amb.negate ? 'ncontains' : 'contains', value: amb.text })
          }
          return {
            label: f.label,
            definition: build({ set, filters: extraFilters, restrict: extraRestrict, period, groups, measures, counting, listing, top, mentioned, sortField, datasetField, freshField }),
          }
        }),
      }
    }

    return { set, definition, unknown, ask, consumed, corrections, timeless, reasons }
  }

  private entryAt(
    entries: Entry[],
    tokens: Token[],
    i: number,
    kinds?: Entry['kind'][],
    tolerant = true
  ): { entry: Entry; length: number; corrected?: string } | null {
    let best: { entry: Entry; length: number } | null = null
    for (const e of entries) {
      if (kinds && !kinds.includes(e.kind)) continue
      if (matchAt(tokens, i, e.stems) && (!best || e.stems.length > best.length || (e.stems.length === best.length && rank(e) < rank(best.entry)))) {
        best = { entry: e, length: e.stems.length }
      }
    }
    if (best || !tolerant) return best
    const t = tokens[i]
    if (!t) return null
    /* Doua greseli numai pe o radacina lunga: "resurselor" are 10 litere, dar radacina `resur` numai 5 - nu e o greseala de tastare a lui "iesire". */
    const budget = Math.min(typoBudget(t.word), t.stem.length >= 8 ? 2 : 1)
    if (budget === 0) return null
    /* Greseala de tastare: un singur cuvant, un singur candidat apropiat. */
    const close: { e: Entry; d: number }[] = []
    for (const e of entries) {
      if (kinds && !kinds.includes(e.kind)) continue
      if (e.stems.length !== 1) continue
      /* Si pe cuvantul intreg: "inchse" are radacina `inch`, dar e la o litera de "inchise". */
      const d = Math.min(distance(t.stem, e.stems[0]!, budget), ...e.forms.filter((f) => !f.includes(' ')).map((f) => distance(t.word, f, budget)))
      if (d <= budget) close.push({ e, d })
    }
    if (close.length === 0) return null
    const min = Math.min(...close.map((c) => c.d))
    let winners = close.filter((c) => c.d === min)
    /*
     * "tichite": numele setului si campul "Tichet" sunt acelasi cuvant - o
     * singura tinta, iar numele setului castiga, ca la potrivirea exacta.
     */
    const named = winners.find((w) => w.e.kind === 'dataset')
    if (named && winners.every((w) => w.e.stems.join(' ') === named.e.stems.join(' '))) winners = [named]
    const targets = new Set(winners.map((w) => `${w.e.kind}:${w.e.field ?? ''}`))
    if (targets.size !== 1) return null
    return { entry: winners[0]!.e, length: 1, corrected: winners[0]!.e.surface }
  }

  private pattern(tokens: Token[], i: number): { length: number; period?: Period; top?: number; compare?: Comparison } | null {
    const t = tokens[i]!
    const now = this.ctx.now
    /* peste 8 ore / mai mult de 3 zile / sub 50% / cel putin 2 GB / peste 3 (incidente) */
    const cmpLen = longestKey(tokens, i, COMPARATOR_STEMS)
    if (cmpLen) {
      const n = tokens[i + cmpLen.length]
      const num = n ? parseNumber(n.word) : null
      if (num !== null) {
        let len = cmpLen.length + 1
        let j = i + len
        if (tokens[j]?.stem === 'de') j++
        const unit = tokens[j] ? UNIT_STEMS.get(tokens[j]!.word) ?? UNIT_STEMS.get(tokens[j]!.stem) : undefined
        if (unit) len = j - i + 1
        const value = unit ? Math.round(num * unit[1] * 100) / 100 : num
        const hinted = COMPARATOR_FIELD_STEMS.get(tokens.slice(i, i + cmpLen.length).map((x) => x.stem).join(' '))
        return { length: len, compare: { op: cmpLen.value, value, unit: unit ? unit[0] : null, ...(hinted ? { field: hinted } : {}) } }
      }
    }
    /* urmatoarele 30 de zile (expirari) */
    const next = longestSet(tokens, i, NEXT_WORDS)
    if (next > 0) {
      const n = tokens[i + next]
      if (n && /^\d{1,3}$/.test(n.word)) {
        let j = i + next + 1
        if (tokens[j]?.stem === 'de') j++
        const day = longestSet(tokens, j, DAY_WORDS)
        if (day > 0) {
          const num = Number(n.word)
          if (num === 7 || num === 30 || num === 90) return { length: j + day - i, period: { key: `next_${num}_days` as Period['key'] } }
          const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + num - 1)
          return { length: j + day - i, period: { key: 'custom', from: iso(new Date(now.getFullYear(), now.getMonth(), now.getDate())), to: iso(end) } }
        }
      }
    }
    /* intervale cu luni si zile: "1-15 septembrie", "3 septembrie" */
    const span = /^(\d{1,2})-(\d{1,2})$/.exec(t.word)
    if (span) {
      const month = monthAt(tokens, i + 1)
      if (month) {
        const year = yearAt(tokens, i + 2)
        const y = year ?? yearFor(month.month, now)
        const a = dayIso(y, month.month, Number(span[1]))
        const b = dayIso(y, month.month, Number(span[2]))
        if (a && b) return { length: year !== null ? 3 : 2, period: { key: 'custom', from: a < b ? a : b, to: a < b ? b : a } }
      }
    }
    if (/^\d{1,2}$/.test(t.word) && (i === 0 || !TOP_WORDS.has(tokens[i - 1]!.stem))) {
      const month = monthAt(tokens, i + 1)
      if (month) {
        const year = yearAt(tokens, i + 2)
        const day = dayIso(year ?? yearFor(month.month, now), month.month, Number(t.word))
        if (day) return { length: year !== null ? 3 : 2, period: { key: 'custom', from: day, to: day } }
      }
    }
    /* ultimele N zile / last N days */
    const last = longestSet(tokens, i, LAST_WORDS)
    if (last > 0) {
      const n = tokens[i + last]
      if (n && /^\d{1,3}$/.test(n.word)) {
        let j = i + last + 1
        if (tokens[j]?.stem === 'de') j++
        const day = longestSet(tokens, j, DAY_WORDS)
        if (day > 0) {
          const num = Number(n.word)
          const key = num === 7 ? 'last_7_days' : num === 30 ? 'last_30_days' : num === 90 ? 'last_90_days' : 'last_n_days'
          return { length: j + day - i, period: key === 'last_n_days' ? { key, n: num } : { key } }
        }
        const week = longestSet(tokens, j, WEEK_WORDS)
        if (week > 0 && Number(n.word) >= 1 && Number(n.word) <= 52) {
          return { length: j + week - i, period: { key: 'last_n_days', n: Number(n.word) * 7 } }
        }
        /* "ultimele 3 luni": de azi inapoi, pana in aceeasi zi de acum 3 luni (fara ea). */
        const months = longestSet(tokens, j, MONTH_WORDS)
        if (months > 0 && Number(n.word) >= 1 && Number(n.word) <= 24) {
          const from = new Date(now.getFullYear(), now.getMonth() - Number(n.word), now.getDate() + 1)
          return { length: j + months - i, period: { key: 'custom', from: iso(from), to: iso(new Date(now.getFullYear(), now.getMonth(), now.getDate())) } }
        }
      }
    }
    /* top 10 / primele 5 */
    const topWord = longestSet(tokens, i, TOP_WORDS)
    if (topWord > 0) {
      const n = tokens[i + topWord]
      if (n && /^\d{1,3}$/.test(n.word) && Number(n.word) > 0) {
        return { length: topWord + 1, top: Math.min(Number(n.word), 100) }
      }
    }
    /* Q3, Q3 2026 */
    const q = /^q([1-4])$/.exec(t.word)
    if (q) {
      const year = yearAt(tokens, i + 1) ?? this.ctx.now.getFullYear()
      const quarter = Number(q[1])
      const from = new Date(year, (quarter - 1) * 3, 1)
      const to = new Date(year, quarter * 3, 0)
      return { length: yearAt(tokens, i + 1) ? 2 : 1, period: { key: 'custom', from: iso(from), to: iso(to) } }
    }
    /* "in martie", "din septembrie 2026", "luna martie" */
    const month = monthAt(tokens, i)
    if (month) {
      const prev = tokens[i - 1]?.word
      const year = yearAt(tokens, i + 1)
      const strong = month.word.length >= 6 || year !== null || prev === 'in' || prev === 'din' || prev === 'luna' || prev === 'during'
      if (strong) {
        const len = year !== null ? 2 : 1
        /* "din martie pana in mai", "march to may" */
        const range = monthRangeAfter(tokens, i + len, month.month, year, now)
        if (range) return { length: len + range.length, period: range.period }
        const y = year ?? yearFor(month.month, now)
        const from = new Date(y, month.month - 1, 1)
        const to = new Date(y, month.month, 0)
        return { length: len, period: { key: 'custom', from: iso(from), to: iso(to) } }
      }
    }
    /* intre 2026-09-01 si 2026-09-15 / intre 1 si 15 septembrie / intre 1 septembrie si 15 octombrie / intre martie si mai */
    const open = longestSet(tokens, i, RANGE_OPEN)
    if (open > 0) {
      const k = i + open
      const d1 = tokens[k] && /^\d{1,2}$/.test(tokens[k]!.word) ? Number(tokens[k]!.word) : null
      if (d1 !== null) {
        const m1 = monthAt(tokens, k + 1)
        const joinAt = m1 ? k + 2 : k + 1
        const join = longestSet(tokens, joinAt, RANGE_JOIN)
        const d2 = join > 0 && tokens[joinAt + join] && /^\d{1,2}$/.test(tokens[joinAt + join]!.word) ? Number(tokens[joinAt + join]!.word) : null
        const m2 = d2 !== null ? monthAt(tokens, joinAt + join + 1) : null
        if (d2 !== null && (m2 || m1)) {
          const endMonth = (m2 ?? m1)!.month
          const startMonth = (m1 ?? m2)!.month
          let end = joinAt + join + (m2 ? 2 : 1)
          const year = yearAt(tokens, end)
          if (year !== null) end++
          const yEnd = year ?? yearFor(endMonth, now)
          const yStart = startMonth <= endMonth ? yEnd : yEnd - 1
          const a = dayIso(yStart, startMonth, d1)
          const b = dayIso(yEnd, endMonth, d2)
          if (a && b && a <= b) return { length: end - i, period: { key: 'custom', from: a, to: b } }
        }
      }
      const mOpen = monthAt(tokens, k)
      if (mOpen) {
        const year = yearAt(tokens, k + 1)
        const range = monthRangeAfter(tokens, k + (year !== null ? 2 : 1), mOpen.month, year, now)
        if (range) return { length: open + (year !== null ? 2 : 1) + range.length, period: range.period }
      }
      const a = dateAt(tokens, i + open)
      if (a) {
        const join = longestSet(tokens, i + open + 1, RANGE_JOIN)
        const b = join > 0 ? dateAt(tokens, i + open + 1 + join) : null
        if (b) return { length: open + 1 + join + 1, period: { key: 'custom', from: a < b ? a : b, to: a < b ? b : a } }
      }
    }
    return null
  }
}

/* ========================================================================= */

function rank(e: Entry): number {
  return { fieldagg: 0, antonym: 1, field: 2, bool: 2, dataset: 3 }[e.kind]
}

function isNumeric(f: CatalogField): boolean {
  return (f.type === 'duration' || f.type === 'bytes' || f.type === 'number') && !f.key.endsWith('_id')
}

function buildEntries(set: CatalogSet): Entry[] {
  const out: Entry[] = []
  const seen = new Map<string, Entry>()
  const push = (phrase: string, entry: Omit<Entry, 'stems' | 'surface' | 'forms'>) => {
    const stems = stemPhrase(phrase).split(' ').filter((s) => s !== '')
    if (stems.length === 0) return
    const key = `${entry.kind}:${entry.field ?? ''}:${stems.join(' ')}`
    const form = fold(phrase)
    const prev = seen.get(key)
    if (prev) {
      if (!prev.forms.includes(form)) prev.forms.push(form)
      return
    }
    const e: Entry = { stems, surface: phrase.toLowerCase(), forms: [form], ...entry }
    seen.set(key, e)
    out.push(e)
  }
  for (const w of [set.label.ro, set.label.en, ...set.syn.ro, ...set.syn.en, ...(SET_NAMES[set.key] ?? [])]) push(w, { kind: 'dataset' })
  for (const f of set.fields) {
    for (const w of [f.label.ro, f.label.en, ...f.syn.ro, ...f.syn.en]) {
      push(w, { kind: 'field', field: f.key })
      /* "timp MEDIU de rezolvare": socoteala in mijlocul numelui. */
      const words = w.split(' ')
      if (words.length >= 2 && isNumeric(f)) {
        for (const [infix, agg] of AGG_INFIX) {
          if (f.aggs.includes(agg)) push([words[0], infix, ...words.slice(1)].join(' '), { kind: 'fieldagg', field: f.key, agg })
        }
      }
    }
  }
  for (const n of FIELD_NAMES) {
    if (n.set !== set.key || !set.fields.some((f) => f.key === n.field)) continue
    for (const w of n.words) push(w, n.agg ? { kind: 'fieldagg', field: n.field, agg: n.agg } : { kind: 'field', field: n.field })
  }
  for (const a of ANTONYMS) {
    if (!set.fields.some((f) => f.key === a.field)) continue
    for (const w of a.words) push(w, { kind: 'antonym', field: a.field, value: a.value })
  }
  return out
}

function buildValues(set: CatalogSet, values: Record<string, ValueItem[]>): ValueRef[] {
  const out: ValueRef[] = []
  for (const f of set.fields) {
    for (const item of [...(values[f.key] ?? []), ...optionItems(f)]) {
      const tokens = tokenize(item.label)
      if (tokens.length > 0) out.push({ field: f.key, stems: tokens.map((t) => t.stem), words: tokens.map((t) => t.word), item })
    }
  }
  return out
}

const AND_WORDS = new Set(['si', 'and', ','])

const NO_BILLABLE_FLAG: Words = {
  ro: 'Nu exista date de facturare pe tichete: nici tichetele, nici timpul lucrat nu au un semn facturabil / nefacturabil. Ce s-a facturat: „facturat luna trecuta pe directie"; timpul lucrat cu tariful lui: „ore facturate pe tehnician".',
  en: 'There is no billing data on tickets: neither tickets nor time worked carry a billable / non-billable flag. What was billed: "billed last month by department"; time worked at its rate: "billable hours by technician".',
}

function noBilling(set: CatalogSet): Words {
  return {
    ro: `„${set.label.ro}" nu au date de facturare. Ce s-a facturat se vede in chargeback: „facturat luna trecuta pe serviciu"; timpul lucrat cu tariful lui: „ore facturate pe tehnician".`,
    en: `"${set.label.en}" has no billing data. What was billed is in chargeback: "billed last month by service"; time worked at its rate: "billable hours by technician".`,
  }
}

/** Dupa cuvintele astea, "noi" e pronumele: "avem noi", "la noi", "pentru noi". */
const NOI_PRONOUN = new Set(['avem', 'am', 'suntem', 'la', 'pentru', 'de', 'ca', 'si', 'cu', 'despre', 'decat'])

/** Urmeaza o valoare reala, direct sau dupa "din", "cu", "de", "la"? */
function valueFollows(refs: ValueRef[], tokens: Token[], i: number): boolean {
  let j = i
  while (j < tokens.length && STOP.has(tokens[j]!.stem) && !AND_WORDS.has(tokens[j]!.stem) && j - i < 2) j++
  return matchValues(refs, tokens, j) !== null
}

function matchAt(tokens: Token[], i: number, stems: string[]): boolean {
  if (i + stems.length > tokens.length) return false
  for (let k = 0; k < stems.length; k++) if (tokens[i + k]!.stem !== stems[k]) return false
  return true
}

/**
 * Valorile reale care incep la `i`: cuvintele omului trebuie sa fie o bucata
 * continua din eticheta ("chrome" din "Google Chrome", "windows 7" din
 * "Microsoft Windows 7 Professional"). Castiga bucata cea mai lunga; pe
 * aceeasi lungime raman toate campurile atinse - intre ele alege omul.
 */
/**
 * Cuvintele care arata a nume propriu: au o litera mare in fraza scrisa de om,
 * o cifra, sau un punct / o cratima / o liniuta jos inauntru. Pozitiile
 * tokenilor sunt in fraza fara diacritice, deci se refac pe fraza originala
 * caracter cu caracter.
 */
function nameLike(text: string, tokens: Token[]): Set<Token> {
  const origin: number[] = []
  for (let j = 0; j < text.length; j++) {
    for (let k = 0; k < fold(text[j]!).length; k++) origin.push(j)
  }
  const out = new Set<Token>()
  for (const t of tokens) {
    const from = origin[t.start]
    const to = origin[t.end - 1]
    if (from === undefined || to === undefined) continue
    const raw = text.slice(from, to + 1)
    if (/\p{Lu}/u.test(raw) || /[0-9._-]/.test(t.word)) out.add(t)
  }
  return out
}

function matchValues(refs: ValueRef[], tokens: Token[], i: number): { length: number; full: boolean; choices: { field: string; ids: number[]; labels: string[] }[] } | null {
  let best = 0
  const hits: { ref: ValueRef; full: boolean }[] = []
  for (const ref of refs) {
    for (let start = 0; start < ref.stems.length; start++) {
      let k = 0
      while (start + k < ref.stems.length && i + k < tokens.length && ref.stems[start + k] === tokens[i + k]!.stem) k++
      if (k === 0) continue
      /*
       * Un singur cuvant cu radacina scurta trebuie sa fie chiar cuvantul din
       * eticheta: "mare" si "Maria" au aceeasi radacina (`mar`), dar nu sunt
       * acelasi lucru.
       */
      if (k === 1 && tokens[i]!.stem.length < 4 && !ref.words.includes(tokens[i]!.word)) continue
      /* Un singur cuvant scurt sau de legatura nu e o valoare ("de", "pe", "nu"). */
      if (k === 1 && (STOP.has(tokens[i]!.stem) || NEGATION.has(tokens[i]!.stem) || tokens[i]!.word.length < 2)) continue
      if (k > best) {
        best = k
        hits.length = 0
      }
      if (k === best) hits.push({ ref, full: k === ref.stems.length })
    }
  }
  if (best === 0) return null
  const byField = new Map<string, { field: string; ids: number[]; labels: string[] }>()
  for (const h of hits) {
    const c = byField.get(h.ref.field) ?? { field: h.ref.field, ids: [], labels: [] }
    if (h.ref.item.id !== null && !c.ids.includes(h.ref.item.id)) c.ids.push(h.ref.item.id)
    if (!c.labels.includes(h.ref.item.label)) c.labels.push(h.ref.item.label)
    byField.set(h.ref.field, c)
  }
  return { length: best, full: hits.some((h) => h.full), choices: [...byField.values()] }
}

function longestSet(tokens: Token[], i: number, set: Set<string>): number {
  for (let len = 3; len >= 1; len--) {
    if (i + len > tokens.length) continue
    const phrase = tokens
      .slice(i, i + len)
      .map((t) => t.stem)
      .join(' ')
    if (set.has(phrase)) return len
  }
  return 0
}

function longestPhrase<T>(tokens: Token[], i: number, table: Map<string, T>): { length: number; value: T } | null {
  for (let len = 4; len >= 1; len--) {
    if (i + len > tokens.length) continue
    const phrase = tokens
      .slice(i, i + len)
      .map((t) => t.stem)
      .join(' ')
    const v = table.get(phrase)
    if (v !== undefined) return { length: len, value: v }
  }
  return null
}

const TIME_STEMS = new Map<string, Period['key']>(TIME_PHRASES.map(([w, k]) => [stemPhrase(w), k]))
const COMPARATOR_FIELD_STEMS = new Map<string, string>(COMPARATOR_FIELDS.map(([w, f]) => [stemPhrase(w), f]))
const COMPARATOR_STEMS = new Map<string, Comparison['op']>(COMPARATORS.map(([w, op]) => [stemPhrase(w), op]))
/* Unitatile se cauta si dupa cuvant, si dupa radacina: "ore", "h", "%". */
const UNIT_STEMS = new Map<string, [NonNullable<Comparison['unit']>, number]>(UNITS.flatMap(([w, u, f]) => [[w, [u, f]] as [string, [NonNullable<Comparison['unit']>, number]], [stemPhrase(w), [u, f]] as [string, [NonNullable<Comparison['unit']>, number]]]))
const ADVERB_BUCKETS = new Map<string, Bucket>(
  [['zilnic', 'day'], ['daily', 'day'], ['saptamanal', 'week'], ['weekly', 'week'], ['lunar', 'month'], ['monthly', 'month'], ['trimestrial', 'quarter'], ['quarterly', 'quarter'], ['anual', 'year'], ['yearly', 'year']].map(([w, b]) => [stemPhrase(w!), b as Bucket])
)
const SORT_MARKERS = new Set(['dupa', 'by', 'sortat dupa', 'sortate dupa', 'ordonat dupa', 'ordonate dupa', 'sorted by', 'ordered by'].map(stemPhrase))

/**
 * Valorile codurilor (stare, gravitate, risc): eticheta in ambele limbi, cu
 * codul ca id. Asa "schimbari aprobate" sau "detectii critice" se inteleg
 * fara sa ceara valori de la server.
 */
export function optionItems(f: CatalogField): ValueItem[] {
  const out: ValueItem[] = []
  for (const o of f.options ?? []) {
    const id = typeof o.value === 'number' ? o.value : /^\d+$/.test(String(o.value)) ? Number(o.value) : null
    if (id === null) continue
    out.push({ id, label: o.label.ro, n: 0 })
    if (o.label.en !== o.label.ro) out.push({ id, label: o.label.en, n: 0 })
  }
  return out
}

/** Se potriveste campul cu unitatea conditiei? */
function fitsComparison(f: CatalogField | undefined, cmp: Comparison): boolean {
  if (!f || !f.filter || f.sensitivity === 'personal') return false
  if (cmp.unit === 'duration') return f.type === 'duration'
  if (cmp.unit === 'bytes') return f.type === 'bytes'
  if (cmp.unit === 'percent') return f.type === 'number' && f.label.en.includes('%')
  return f.type === 'number' && !f.key.endsWith('_id')
}

function parseNumber(word: string): number | null {
  if (!/^\d{1,9}(\.\d{1,3})?$/.test(word)) return null
  return Number(word)
}

function longestKey<T>(tokens: Token[], i: number, table: Map<string, T>): { length: number; value: T } | null {
  return longestPhrase(tokens, i, table)
}

/** Anul unei luni fara an: luna care n-a venit inca anul asta e a anului trecut. */
function yearFor(month: number, now: Date): number {
  return month > now.getMonth() + 1 ? now.getFullYear() - 1 : now.getFullYear()
}

function dayIso(y: number, m: number, d: number): string | null {
  const date = new Date(y, m - 1, d)
  return date.getMonth() === m - 1 && date.getDate() === d ? iso(date) : null
}

/** Dupa o luna: "pana in mai", "si mai", "to may" - intervalul intreg intre cele doua luni. */
function monthRangeAfter(tokens: Token[], i: number, startMonth: number, startYear: number | null, now: Date): { length: number; period: Period } | null {
  const join = longestSet(tokens, i, RANGE_JOIN)
  if (join === 0) return null
  let j = i + join
  if (tokens[j]?.word === 'in' || tokens[j]?.word === 'la') j++
  const end = monthAt(tokens, j)
  if (!end) return null
  const endYear = yearAt(tokens, j + 1)
  const yEnd = endYear ?? (startYear !== null && end.month >= startMonth ? startYear : yearFor(end.month, now))
  const yStart = startYear ?? (startMonth <= end.month ? yEnd : yEnd - 1)
  const from = new Date(yStart, startMonth - 1, 1)
  const to = new Date(yEnd, end.month, 0)
  if (from > to) return null
  return { length: j + (endYear !== null ? 2 : 1) - i, period: { key: 'custom', from: iso(from), to: iso(to) } }
}
const AGG_STEMS = new Map<string, Measure['agg']>(AGG_WORDS.map(([w, a]) => [stemPhrase(w), a]))
const BUCKET_STEMS = new Map<string, Bucket>(BUCKETS.map(([w, b]) => [stemPhrase(w), b]))
const MONTH_STEMS = new Map<string, number>(MONTHS.flatMap(([words, m]) => words.map((w) => [w, m] as [string, number])))

function bucketAt(tokens: Token[], i: number): { length: number; bucket: Bucket } | null {
  const hit = longestPhrase(tokens, i, BUCKET_STEMS)
  return hit ? { length: hit.length, bucket: hit.value } : null
}

function monthAt(tokens: Token[], i: number): { month: number; word: string } | null {
  const t = tokens[i]
  if (!t) return null
  const m = MONTH_STEMS.get(t.word)
  return m ? { month: m, word: t.word } : null
}

function yearAt(tokens: Token[], i: number): number | null {
  const t = tokens[i]
  return t && /^20\d\d$/.test(t.word) ? Number(t.word) : null
}

function dateAt(tokens: Token[], i: number): string | null {
  const t = tokens[i]
  if (!t) return null
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t.word)
  if (m) return `${m[1]}-${m[2]}-${m[3]}`
  m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(t.word)
  if (m) return `${m[3]}-${m[2]!.padStart(2, '0')}-${m[1]!.padStart(2, '0')}`
  return null
}

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

interface BuildInput {
  set: CatalogSet
  filters: Filter[]
  restrict: Restrict
  period: Period | undefined
  groups: { field: string; bucket?: Bucket }[]
  measures: Measure[]
  counting: boolean
  listing: boolean
  top: number | null
  mentioned: string[]
  sortField?: string | null
  datasetField?: string | null
  /** Campul de creare, cand fraza cere ce e nou. */
  freshField?: string | null
}

/**
 * Definitia, dupa reguli fixe de vizualizare: numar fara grupare -> cifra;
 * grupare pe timp -> linie; pe categorie -> bare; "top N" -> top; "care",
 * "lista" sau nicio socoteala -> tabel.
 */
function build(input: BuildInput): Definition {
  const { set } = input
  const def: Definition = { dataset: set.key }
  if (input.filters.length > 0) def.filters = input.filters
  if (Object.keys(input.restrict).length > 0) def.restrict = input.restrict
  if (input.period) {
    def.period = input.period
    /* "cu suport pana la luna viitoare": perioada pe campul de data pomenit. */
    const dated = [...input.mentioned].reverse().find((k) => set.fields.find((f) => f.key === k)?.type === 'date')
    if (dated && dated !== set.time) def.time_field = dated
    const hint = TIME_FIELD_HINTS.find((h) => input.filters.some((f) => f.field === h.field && f.op === 'eq' && f.value === h.value))
    if (!dated && hint && set.fields.some((f) => f.key === hint.timeField) && hint.timeField !== set.time) def.time_field = hint.timeField
    if (!dated && input.freshField && input.freshField !== set.time) def.time_field = input.freshField
  }

  let measures = [...input.measures]
  const groups = [...input.groups].slice(0, 2)
  const fields = new Map(set.fields.map((f) => [f.key, f]))

  const sortField = input.sortField ?? null
  if (sortField !== null && groups.length === 0 && measures.length === 0 && !input.counting) {
    const pickGroup = input.top !== null ? (input.mentioned.find((k) => fields.get(k)?.group) ?? (input.datasetField && fields.get(input.datasetField)?.group ? input.datasetField : undefined)) : undefined
    if (pickGroup && fields.get(sortField)?.aggs.includes('sum')) {
      /* "top 10 aplicatii dupa timp de folosire": pe aplicatie, suma timpului. */
      measures = [{ agg: 'sum', field: sortField }]
      groups.push({ field: pickGroup })
    } else {
      /* "top 5 probleme dupa incidente legate": lista, ordonata dupa camp. */
      const extra = [...input.mentioned, sortField].filter((k, n, all) => !set.columns.includes(k) && all.indexOf(k) === n)
      if (extra.length > 0) def.columns = [...set.columns, ...extra]
      def.sort = { by: sortField, dir: 'desc' }
      if (input.top !== null) def.limit = input.top
      def.view = 'table'
      return def
    }
  }

  if (input.top !== null && groups.length === 0) {
    /* "top 10 aplicatii" - grupat pe campul pomenit, altfel pe primul camp grupabil al setului. */
    const pick = input.mentioned.find((k) => fields.get(k)?.group) ?? set.columns.find((k) => fields.get(k)?.group && fields.get(k)?.type !== 'date')
    if (pick) groups.push({ field: pick })
  }

  const filtersOnDefault =
    (set.default_measure?.field !== undefined && input.filters.some((f) => f.field === set.default_measure!.field)) ||
    input.filters.some((f) => f.op === 'gt' || f.op === 'gte' || f.op === 'lt' || f.op === 'lte')
  const aggregate = input.counting || measures.length > 0 || groups.length > 0 || input.top !== null
  if (!aggregate && !input.listing && set.default_measure && !filtersOnDefault) {
    measures = [set.default_measure]
  }
  const isAggregate = aggregate || measures.length > 0

  if (isAggregate) {
    if (measures.length === 0) {
      measures = set.default_measure && !filtersOnDefault && !input.counting ? [set.default_measure] : [{ agg: 'count' }]
    }
    def.measures = measures
    if (groups.length > 0) def.group_by = groups
    let view: View
    if (input.top !== null && groups.length > 0) {
      view = 'topn'
      def.sort = { by: 'm0', dir: 'desc' }
      def.limit = input.top
    } else if (groups.length === 0) {
      view = 'kpi'
    } else if (groups[0]!.bucket) {
      view = 'line'
    } else {
      view = 'bar'
    }
    def.view = view
  } else {
    /* Ce e nou se vede cu data adaugarii langa el. */
    const extra = [...input.mentioned, ...(input.freshField ? [input.freshField] : [])].filter((k, n, all) => !set.columns.includes(k) && all.indexOf(k) === n)
    if (extra.length > 0) def.columns = [...set.columns, ...extra]
    def.view = 'table'
  }
  return def
}

export function emptyResult(): ParseResult {
  return { status: 'empty', unknown: [], corrections: [] }
}

/** Pentru teste si pentru ecran: parsarea unei fraze, intr-un singur apel. */
export function parsePhrase(text: string, ctx: ParseContext): ParseResult {
  return new PhraseParser(ctx).parse(text)
}

export function labelOf(words: Words, locale: Locale): string {
  return words[locale]
}

export { stem }
